<template>
    <v-row>
        <v-col cols="12" md="9">
            <h3 class='mb-3'>{{ $t('efficiency.title') }}</h3>
            <p>{{ $t('efficiency.subtitle') }}</p>
            <apexchart type=scatter height=420 :options="optionsProfitTime" :series="seriesProfitTime"/>
        </v-col>

        <v-col cols="12" md="3">
            <div class="d-flex justify-center text-center">
                <div>
                    <h3 class='mb-3'>{{ $t('distribution.title') }}</h3>
                    <p>{{ $t('distribution.subtitle') }}</p>
                </div>
            </div>
            <apexchart type=bar height="400" :options="optionsDistribution" :series="seriesDistribution"/>
        </v-col>
    </v-row>
</template>

<script>
    export default {
        name: "SideStats",
        data() {
            return {
                durationInSeconds: true,

                // Charts
                optionsProfitTime: {
                    theme:{
                        mode:this.$store.state.currentTheme == "dark"? "dark": "light"
                    },
                    noData: {
                        text: 'No data for the current filter',
                        align: 'center',
                        verticalAlign: 'middle',
                        style: {
                            color: '#111111',
                            fontSize: '21px',
                            fontFamily: 'Ubuntu, sans-serif',
                        }
                    },
                    colors:['#008CFF', '#FF7300'],
                    chart: {
                        height: 350,
                        type: 'scatter',
                        toolbar: {
                            show: true,
                            offsetX: 0,
                            offsetY: 0,
                            tools: {
                                download: true,
                                selection: true,
                                zoom: true,
                                zoomin: true,
                                zoomout: true,
                                pan: true,
                                customIcons: []
                            },
                            autoSelected: 'zoom'
                        },
                    },
                    xaxis: {
                        tickAmount: 12,
                        type: 'category',
                        title: {
                            text: 'Trade Duration',
                        },
                        labels: {
                            formatter: function (value) {
                                return `${value.toFixed(0)}`;
                            },
                            style: {
                                color: '#6C7079',
                                fontSize: '12px'
                            },
                        }
                    },
                    yaxis: {
                        title: {
                            text: 'Monetary Profit'
                        },
                        labels: {
                            formatter: function (value) {
                              let numberValue = (Math.abs(value) >= 1000000)? `${Math.abs(value / 1000000).toFixed(1)}M` :
                                  (Math.abs(value) >= 1000)? `${Math.abs(value / 1000).toFixed(1)}k` : Math.abs(value.toFixed(0));
                              return (value >= 0)? `$${numberValue}` : `-$${numberValue}`;
                            },
                            style: {
                                color: '#6C7079',
                                fontSize: '12px'
                            },
                        }
                    },
                    tooltip: {
                        x: {
                            formatter: function(val) {
                                return `${val.toFixed(1)} mins`;
                            },
                        }
                    }
                },
                seriesProfitTime: [],

                optionsDistribution: {
                    theme:{
                        mode:this.$store.state.currentTheme == "dark"? "dark": "light"
                    },
                    noData: {
                        text: 'No data for the current filter',
                        align: 'center',
                        verticalAlign: 'middle',
                        style: {
                            color: '#111111',
                            fontSize: '21px',
                            fontFamily: 'Ubuntu, sans-serif',
                        }
                    },
                    colors:['rgb(36,120,81)', '#FF4747'],
                    chart: {
                        type: 'bar',
                        stacked: true,
                        stackType: '100%'
                    },
                    plotOptions: {
                        bar: {
                            horizontal: true,
                        },
                    },
                    stroke: {
                        width: 1,
                        colors: ['#fff']
                    },
                    xaxis: {
                        categories: ['Buy', 'Sell'],
                        labels: {
                            formatter: function (value) {
                                return `${value}%`;
                            },
                        },
                    },
                    yaxis: {
                        labels: {
                            style: {
                                colors: ['#008CFF', '#FF7300'],
                                fontSize: '12px',
                                fontWeight: 500,
                            },
                        },
                    },
                    tooltip: {
                        y: {
                            formatter: function (val) {
                                return `$ ${val.toFixed(2)}`;
                            }
                        }
                    },
                    fill: {
                        opacity: 1
                    },
                    legend: {
                        position: 'top',
                        horizontalAlign: 'center',
                        offsetX: 40
                    },
                    grid: {
                        xaxis: {
                            lines: {
                                show: false
                            }
                        }
                    },
                },
                seriesDistribution: [],
            };
        },
        mounted() {
            this.setGraphs();
        },
        watch: {
            '$store.state.book.trades': function() {
                this.setGraphs();
            }
        },
        methods: {
            setGraphs() {
                this.seriesProfitTime = [];
                this.seriesDistribution = [];
                let copyOfTrades = this.$store.state.book.trades.slice();
                let tradesBySide = this.groupBy(copyOfTrades, 'side');
                let tradesBySideArray = Object.entries(tradesBySide);
                let buyWinners = 0;
                let sellWinners = 0;
                let buyLosers = 0;
                let sellLosers = 0;

                tradesBySideArray.forEach((tradesOfSide) =>{
                    let name = tradesOfSide[0];
                    let data = [];
                    tradesOfSide[1].forEach((trade) => {
                        //data.push([this.getSecondsFromDuration(trade.tradeTime), trade.netProfit]);
                        data.push([this.getMinutesFromDuration(trade.tradeTime), trade.netProfit]);

                        if(trade.netProfit > 0 && trade.side === 'Buy') buyWinners += trade.netProfit;
                        else if(trade.netProfit <= 0 && trade.side === 'Buy') buyLosers += Math.abs(trade.netProfit);
                        else if(trade.netProfit > 0 && trade.side === 'Sell') sellWinners += trade.netProfit;
                        else sellLosers += Math.abs(trade.netProfit);
                    });
                    this.seriesProfitTime.push({name, data});
                });

                this.seriesDistribution.push({ name: 'Winners', data: [ buyWinners, sellWinners ]});
                this.seriesDistribution.push({ name: 'Losers', data: [ buyLosers, sellLosers ]});
            },
            groupBy (xs, key) {
                return xs.reduce(function(rv, x) {
                    (rv[x[key]] = rv[x[key]] || []).push(x);
                    return rv;
                }, {});
            },
            getSecondsFromDuration(duration) {
                let seconds = (duration.seconds * 1);
                seconds += (duration.minutes * 1) * 60;
                seconds += (duration.hours * 1) * 3600;
                seconds += (duration.days * 1) * 86400;
                seconds += (duration.weeks * 1) * 604800;
                seconds += (duration.months * 1) * 2419200;
                seconds += (duration.years * 1) * 29030400;
                return seconds;
            },
            getMinutesFromDuration(duration) {
                let minutes = (duration.seconds * 1) / 60;
                minutes += (duration.minutes * 1);
                minutes += (duration.hours * 1) * 60;
                minutes += (duration.days * 1) * 1440;
                minutes += (duration.weeks * 1) * 10080;
                minutes += (duration.months * 1) * 40320;
                minutes += (duration.years * 1) * 483840;
                return minutes;
            },
        }
    }
</script>



<style scoped>
    .switch-button {
        font-size: 15px;
        color: #ffffff !important;
        background-color: #111111 !important;
        border: 0 !important;
    }
</style>
